import React, { useState, useEffect, useMemo } from 'react'
import { isEmpty } from 'lodash'
import { Dialog, IconButton } from '@material-ui/core'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { ReactComponent as RightArrow } from 'assets/img/right-arrow.svg'
import { ReactComponent as LeftArrow } from 'assets/img/left-arrow.svg'
import { ReactComponent as CloseIcon } from 'assets/img/close-icon.svg'
import { ReactComponent as FullScreenIcon } from 'assets/img/full-screen-icon.svg'
import Checkbox from 'components/common/Checkbox'
import Radio from 'components/common/Radio'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import {
  ImageInfoContainer,
  ArrowIconButton,
  StyledIconButton,
  FullSizeDescription,
  FullSizeTitle,
  FullSizeHeader,
  Image,
  ImagesContainer,
  ImageTitle,
  ImageHeader,
  DialogBody,
  DialogBackdrop,
  FullSizeImage,
} from './ImagesList.styles'
import { ARROW_LEFT, ARROW_RIGHT } from './ImagesList.constants'
import { ImagesListProps, Option } from './ImagesList.types'

function ImagesList({
  options,
  isMultiple,
  value,
  onItemClick,
  disabled,
}: ImagesListProps) {
  const [fullScreenItem, setFullScreenItem] = useState<
    Option & { index: number }
  >({} as Option & { index: number })
  const isPreviousItemExist = useMemo(() => fullScreenItem.index > 0, [
    fullScreenItem,
  ])

  const isNextItemExist = useMemo(
    () => fullScreenItem.index + 1 !== Object.keys(options).length,
    [fullScreenItem, options],
  )

  const handleArrowClick = ({ isNextItem = false }) => {
    const newIndex = fullScreenItem.index + (isNextItem ? 1 : -1)

    setFullScreenItem({
      ...options[Object.keys(options)[newIndex] as keyof typeof Option],
      index: newIndex,
    })
  }

  const handleKeyClick = ({ key }: KeyboardEvent) => {
    if (key === ARROW_LEFT && isPreviousItemExist) {
      handleArrowClick({ isNextItem: false })
    }

    if (key === ARROW_RIGHT && isNextItemExist) {
      handleArrowClick({ isNextItem: true })
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyClick)

    return () => window.removeEventListener('keydown', handleKeyClick)
  })

  const counter = useMemo(
    () => `${fullScreenItem.index + 1}/${Object.keys(options).length}`,
    [options, fullScreenItem],
  )
  const isMobileView = ifWindowWidthLessThan(TABLET_BREAKPOINT)

  return (
    <>
      <ImagesContainer shouldWrap={!isMobileView}>
        {Object.keys(options).map((item, index) => {
          const { url, title } = options[item as keyof typeof Option]
          const itemExist = !isEmpty(value) && item in value

          return (
            <Image
              key={`${url}${item}`}
              onClick={() => !disabled && onItemClick({ [item]: title })}
              imageUrl={url}
              active={itemExist}
              disabled={disabled}>
              <ImageHeader justify="space-between">
                {isMultiple ? (
                  <Checkbox disabled={disabled} checked={itemExist} />
                ) : (
                  <Radio disabled={disabled} checked={itemExist} />
                )}
                <StyledIconButton>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation()

                      setFullScreenItem({
                        index,
                        ...options[item as keyof typeof Option],
                      })
                    }}
                    aria-label="open full size view">
                    <FullScreenIcon />
                  </IconButton>
                </StyledIconButton>
              </ImageHeader>
              <ImageTitle>{title}</ImageTitle>
            </Image>
          )
        })}
      </ImagesContainer>
      <Dialog
        open={!isEmpty(fullScreenItem) && !Number.isNaN(fullScreenItem.index)}
        BackdropComponent={DialogBackdrop}
        PaperComponent={({ children }) => (
          <DialogBody align="center" direction="row" justify="center">
            {children}
          </DialogBody>
        )}>
        {!isEmpty(fullScreenItem) && !Number.isNaN(fullScreenItem.index) && (
          <>
            {isMobileView && (
              <FullSizeHeader isMobileView>
                {counter}
                <IconButton
                  onClick={() =>
                    setFullScreenItem({} as Option & { index: number })
                  }
                  aria-label="close full size view">
                  <CloseIcon />
                </IconButton>
              </FullSizeHeader>
            )}
            {isPreviousItemExist && (
              <ArrowIconButton $isLeft $isMobileView={isMobileView}>
                <IconButton
                  onClick={() => handleArrowClick({ isNextItem: false })}
                  aria-label="left arrow">
                  <LeftArrow />
                </IconButton>
              </ArrowIconButton>
            )}
            <FullSizeImage
              direction="column"
              isFirstImage={!isPreviousItemExist}
              isLastImage={!isNextItemExist}
              isMobileView={isMobileView}>
              {!isMobileView && (
                <FullSizeHeader>
                  {counter}
                  <IconButton
                    onClick={() =>
                      setFullScreenItem({} as Option & { index: number })
                    }
                    aria-label="close full size view">
                    <CloseIcon />
                  </IconButton>
                </FullSizeHeader>
              )}
              <img src={fullScreenItem.url} alt="full screen view" />
              <ImageInfoContainer
                isMobileView={isMobileView}
                direction="column">
                <FullSizeTitle>{fullScreenItem.title}</FullSizeTitle>
                <FullSizeDescription>
                  {fullScreenItem.description}
                </FullSizeDescription>
              </ImageInfoContainer>
            </FullSizeImage>
            {isNextItemExist && (
              <ArrowIconButton $isMobileView={isMobileView}>
                <IconButton
                  onClick={() => handleArrowClick({ isNextItem: true })}
                  aria-label="right arrow">
                  <RightArrow />
                </IconButton>
              </ArrowIconButton>
            )}
          </>
        )}
      </Dialog>
    </>
  )
}

export default ImagesList
